import React from 'react';

import { Button, Flex, Modal, Stack, TextInput, Textarea, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH, validateDescription, validateTitle } from '../constants/constants';
import type { ExperimentResponse } from '../generated/generatedOpenAPI';
import { useUpdateExperiment } from '../store/store';

interface UpdateExperimentFormValues {
  title: string;
  description: string;
}

interface EditExperimentModalProps {
  opened: boolean;
  close: () => void;
  experiment: ExperimentResponse;
}

export function EditExperimentModal({ opened, close, experiment }: EditExperimentModalProps) {
  const theme = useMantineTheme();

  const form = useForm({
    initialValues: {
      title: experiment.title,
      description: experiment.description || '',
    },
    validate: {
      title: validateTitle,
      description: validateDescription,
    },
  });

  const titleError = validateTitle(form.values.title);
  const descriptionError = validateDescription(form.values.description);

  const [updateExperiment, { isLoading: isUpdatingExperiment }] = useUpdateExperiment();

  const handleUpdateExperiment = React.useCallback(
    async (values: UpdateExperimentFormValues) => {
      try {
        const { title, description } = values;
        if (!experiment.id) {
          throw new Error('Experiment ID is required');
        }
        await updateExperiment({
          id: experiment.id,
          experimentUpdate: {
            title,
            description,
          },
        });
        showNotification({
          title: 'Experiment updated',
          message: 'Experiment has been updated successfully',
          color: theme.colors.green[7],
          autoClose: 2000,
        });
        close();
      } catch (error) {
        let errorMessage;
        if (error instanceof Error && 'data' in error && 'detail' in (error as any).data) {
          errorMessage = `Experiment could not be updated: ${(error as any).data.detail}`;
        } else {
          errorMessage = 'Experiment could not be updated';
        }
        showNotification({
          title: 'Failed to update experiment',
          message: errorMessage,
          color: theme.colors.red[7],
          autoClose: 2000,
        });
      }
    },
    [experiment.id, updateExperiment, theme.colors.green, theme.colors.red, close],
  );

  return (
    <Modal data-testid="edit-experiment-modal" opened={opened} onClose={close} title="Edit experiment" closeOnClickOutside={false} size="lg">
      <form onSubmit={form.onSubmit(handleUpdateExperiment)}>
        <Stack gap="md" h="100%" justify="space-between">
          <Stack gap="xl">
            <TextInput
              data-testid="edit-experiment-title-input"
              label="Title"
              description={`Give your experiment a title (${form.values.title?.length ?? 0}/${TITLE_MAX_LENGTH})`}
              placeholder="Enter experiment title"
              required
              withAsterisk
              maxLength={TITLE_MAX_LENGTH + 1}
              {...form.getInputProps('title')}
              error={titleError}
            />
            <Textarea
              data-testid="edit-experiment-description-input"
              label="Description"
              description={`Describe your experiment in a few words (${form.values.description?.length ?? 0}/${DESCRIPTION_MAX_LENGTH})`}
              placeholder="Enter experiment description"
              rows={5}
              maxLength={DESCRIPTION_MAX_LENGTH + 1}
              {...form.getInputProps('description')}
              error={descriptionError}
            />
          </Stack>

          <Flex gap="md" justify="flex-end">
            <Button variant="subtle" onClick={close}>
              Cancel
            </Button>
            <Button
              data-testid="edit-experiment-submit-button"
              variant="filled"
              type="submit"
              loading={isUpdatingExperiment}
              disabled={Boolean(titleError || descriptionError)}
            >
              Save
            </Button>
          </Flex>
        </Stack>
      </form>
    </Modal>
  );
}
